/* Piezas visuales del Análisis de cliente (diseño por tarjetas): pasos, zona para arrastrar archivos con progreso,
   mini curvas, gráfico de deuda mes a mes y de días de atraso por año. Solo presentación: no calculan nada del análisis.
   Colores y tema noche en rf.css (clases acm-*). */
import { useMemo, useState } from "react";
import { Check, FileSpreadsheet, FileText, Upload } from "lucide-react";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesTxt = (p) => (p && p.length >= 7 ? `${MESES[Number(p.slice(5, 7)) - 1]} ${p.slice(2, 4)}` : p || "");
export const usdCorto = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n), a = Math.abs(v), s = v < 0 ? "−" : "";
  if (a >= 1e6) return `${s}US$ ${(a / 1e6).toLocaleString("es-PY", { maximumFractionDigits: 1 })} M`;
  if (a >= 1e4) return `${s}US$ ${Math.round(a / 1e3).toLocaleString("es-PY")} mil`;
  return `${s}US$ ${Math.round(a).toLocaleString("es-PY")}`;
};

/* ---------------------------------------------------------------- pasos del análisis */
const PASOS = ["Balance", "Cliente y datos", "Resultado", "Decisión"];
export function Pasos({ paso }) {
  return (
    <ol className="acm-pasos" aria-label="Pasos del análisis">
      {PASOS.map((t, i) => {
        const n = i + 1;
        const est = n < paso ? "hecho" : n === paso ? "actual" : "falta";
        return (
          <li key={t} className={`acm-paso acm-paso-${est}`} aria-current={est === "actual" ? "step" : undefined}>
            <span className="acm-paso-n">{est === "hecho" ? <Check className="h-3.5 w-3.5" /> : n}</span>
            <span>{t}</span>
          </li>
        );
      })}
    </ol>
  );
}

/* ---------------------------------------------------------------- zona de carga (arrastrar y soltar) */
const esPdf = (f) => /\.pdf$/i.test(f.name) || f.type === "application/pdf";
const esExcel = (f) => /\.xls[xm]?$/i.test(f.name);

/* onArchivos(files, esPdf): Excel → uno; PDF → varios (uno por año). avance = {pct (0-100 o null), txt} */
export function ZonaCarga({ busy, avance, onArchivos, onElegirExcel, onElegirPdf, leido }) {
  const [encima, setEncima] = useState(false);
  const soltar = (e) => {
    e.preventDefault();
    setEncima(false);
    if (busy) return;
    const fl = Array.from(e.dataTransfer?.files || []);
    const pdfs = fl.filter(esPdf);
    if (pdfs.length) return onArchivos(pdfs, true);
    const xl = fl.find(esExcel);
    if (xl) return onArchivos([xl], false);
    onArchivos([], false, "Ese archivo no es un Excel ni un PDF.");
  };
  const pct = avance?.pct;
  return (
    <div className="space-y-2">
      <div className={"acm-zona" + (encima ? " acm-zona-encima" : "") + (busy ? " acm-zona-ocupada" : "")}
        onDragOver={(e) => { e.preventDefault(); if (!encima) setEncima(true); }} onDragLeave={() => setEncima(false)} onDrop={soltar}
        data-testid="ac-zona-carga">
        <Upload className="acm-zona-icono" aria-hidden="true" />
        <div className="acm-zona-t">{leido ? "Arrastrá otro balance para reemplazarlo" : "Arrastrá acá el balance del cliente"}</div>
        <div className="acm-zona-s">Excel del sistema Tecnomyl (ListadoClientesBalanceTM .xls/.xlsx/.csv), o PDF digital o escaneado (podés soltar varios PDF, uno por año)</div>
        <div className="flex flex-wrap justify-center gap-2 pt-1">
          <button type="button" disabled={busy} onClick={onElegirExcel} className="acm-boton-sec" data-testid="ac-upload"><FileSpreadsheet className="h-4 w-4" /> Elegir Excel</button>
          <button type="button" disabled={busy} onClick={onElegirPdf} className="acm-boton-sec" data-testid="ac-upload-pdf"><FileText className="h-4 w-4" /> Elegir PDF</button>
        </div>
      </div>
      {busy && avance?.txt && (
        <div className="acm-progreso" role="status" aria-live="polite" data-testid="ac-progreso">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="font-medium">{avance.txt}</span>
            {pct != null && <span className="tabular-nums font-semibold">{Math.round(pct)}%</span>}
          </div>
          <div className="acm-barra"><div className={pct == null ? "acm-barra-llena acm-barra-indef" : "acm-barra-llena"} style={pct == null ? undefined : { width: `${Math.max(4, Math.min(100, pct))}%` }} /></div>
          <div className="text-[11px] opacity-75">Un PDF escaneado puede tardar unos minutos; no cierres esta pantalla.</div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- mini curva para los números clave */
export function MiniCurva({ valores, mal = false }) {
  const v = (valores || []).filter((x) => x !== null && x !== undefined && !Number.isNaN(Number(x))).map(Number);
  if (v.length < 2) return null;
  const mn = Math.min(...v), mx = Math.max(...v), r = mx - mn || 1;
  const pts = v.map((x, i) => `${(4 + (i * 88) / (v.length - 1)).toFixed(1)},${(30 - ((x - mn) / r) * 24).toFixed(1)}`).join(" ");
  const ult = pts.split(" ").pop().split(",");
  return (
    <svg width="96" height="34" viewBox="0 0 96 34" aria-hidden="true" className={"acm-curva" + (mal ? " acm-curva-mal" : "")}>
      <polyline points={pts} fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={ult[0]} cy={ult[1]} r="3" />
    </svg>
  );
}

/* ---------------------------------------------------------------- deuda mes a mes */
export function GraficoDeuda({ serie }) {
  const [sel, setSel] = useState(null);
  const d = useMemo(() => {
    const s = (serie || []).filter((x) => x && x.periodo);
    if (!s.length) return null;
    const W = 600, H = 220, L = 52, R = 14, T = 16, B = 30;
    const mx = Math.max(1, ...s.map((x) => x.deuda || 0), ...s.map((x) => x.vencido || 0));
    const paso = Math.pow(10, Math.floor(Math.log10(mx)));
    const tope = Math.ceil(mx / paso) * paso;
    const X = (i) => L + (s.length === 1 ? (W - L - R) / 2 : (i * (W - L - R)) / (s.length - 1));
    const Y = (v) => T + (1 - (v || 0) / tope) * (H - T - B);
    const linea = (k) => s.map((x, i) => `${X(i).toFixed(1)},${Y(x[k]).toFixed(1)}`).join(" ");
    const area = `M${X(0)},${Y(0)} ` + s.map((x, i) => `L${X(i).toFixed(1)},${Y(x.deuda).toFixed(1)}`).join(" ") + ` L${X(s.length - 1)},${Y(0)} Z`;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: Y(tope * f), t: usdCorto(tope * f).replace("US$ ", "") }));
    return { s, W, H, X, Y, linea, area, ticks, B };
  }, [serie]);
  if (!d) return <p className="acm-vacio">No hay deuda mes a mes para mostrar: el sistema no tiene la cartera de este cliente y no se cargó el historial de compras.</p>;
  const i = sel ?? d.s.length - 1;
  const p = d.s[i];
  return (
    <div className="acm-grafico">
      <div className="acm-leyenda">
        <span><i className="acm-ley-deuda" /> Deuda con nosotros</span><span><i className="acm-ley-venc" /> Vencido</span>
        <span className="ml-auto tabular-nums"><b>{mesTxt(p.periodo)}</b> · {usdCorto(p.deuda)} · vencido {usdCorto(p.vencido)}</span>
      </div>
      <svg viewBox={`0 0 ${d.W} ${d.H}`} className="w-full h-auto" role="img" aria-label="Evolución de la deuda con nosotros y lo vencido, mes a mes"
        onMouseLeave={() => setSel(null)}>
        {d.ticks.map((t) => (
          <g key={t.y}><line x1="52" x2={d.W - 14} y1={t.y} y2={t.y} className="acm-rejilla" /><text x="46" y={t.y + 4} textAnchor="end" className="acm-eje">{t.t}</text></g>
        ))}
        <path d={d.area} className="acm-area" />
        <polyline points={d.linea("deuda")} className="acm-linea-deuda" />
        <polyline points={d.linea("vencido")} className="acm-linea-venc" />
        {d.s.map((x, k) => (
          <g key={x.periodo}>
            <rect x={d.X(k) - 18} y="0" width="36" height={d.H - d.B} fill="transparent" onMouseEnter={() => setSel(k)} onFocus={() => setSel(k)} tabIndex={0}
              aria-label={`${mesTxt(x.periodo)}: deuda ${usdCorto(x.deuda)}, vencido ${usdCorto(x.vencido)}`} />
            {(d.s.length <= 8 || k % 2 === (d.s.length - 1) % 2) && <text x={d.X(k)} y={d.H - 10} textAnchor="middle" className="acm-eje">{mesTxt(x.periodo)}</text>}
          </g>
        ))}
        <line x1={d.X(i)} x2={d.X(i)} y1="16" y2={d.H - d.B} className="acm-guia" />
        <circle cx={d.X(i)} cy={d.Y(p.deuda)} r="5.5" className="acm-punto" />
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------------- días de atraso por año */
export function GraficoPagos({ anios, tol = 30 }) {
  const a = (anios || []).slice(-6);
  if (!a.length) return <p className="acm-vacio">Cargá el historial de compras (PDF, Excel o a mano) para ver cómo pagó año por año.</p>;
  const W = 600, H = 220, L = 40, R = 14, T = 22, B = 30;
  const mx = Math.max(tol * 1.4, ...a.map((x) => x.dias_prom || 0)) * 1.1;
  const Y = (v) => T + (1 - v / mx) * (H - T - B);
  const ancho = Math.min(72, ((W - L - R) / a.length) * 0.55);
  const X = (i) => L + ((i + 0.5) * (W - L - R)) / a.length;
  return (
    <div className="acm-grafico">
      <div className="acm-leyenda"><span>Días de atraso promedio por año (pesa más lo que compró)</span><span className="ml-auto"><i className="acm-ley-tol" /> {tol} días de tolerancia</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`Días de atraso promedio por año: ${a.map((x) => `${x.anio} ${x.dias_prom} días`).join(", ")}`}>
        <line x1={L} x2={W - R} y1={H - B} y2={H - B} className="acm-rejilla" />
        {a.map((x, i) => {
          const malo = (x.dias_prom || 0) > tol;
          const y = Y(x.dias_prom || 0);
          return (
            <g key={x.anio}>
              <rect x={X(i) - ancho / 2} y={y} width={ancho} height={Math.max(2, H - B - y)} rx="6" className={malo ? "acm-barra-mal" : "acm-barra-bien"} style={{ animationDelay: `${0.07 * i}s` }}>
                <title>{`${x.anio}: ${x.dias_prom} días promedio, máximo ${x.dias_max} · compró ${usdCorto(x.comprado)}`}</title>
              </rect>
              <text x={X(i)} y={y - 6} textAnchor="middle" className={malo ? "acm-valor acm-valor-mal" : "acm-valor"}>{x.dias_prom} d</text>
              <text x={X(i)} y={H - 10} textAnchor="middle" className="acm-eje">{x.anio}</text>
            </g>
          );
        })}
        <line x1={L} x2={W - R} y1={Y(tol)} y2={Y(tol)} className="acm-tolerancia" />
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------------- tarjeta «Cómo viene con nosotros» */
export function ComoViene({ cp, tol = 30 }) {
  const [ver, setVer] = useState("deuda");
  if (!cp) return null;
  const serie = cp.serie12?.length ? cp.serie12 : cp.serie || [];
  const anios = cp.historial?.por_anio || [];
  const color = cp.sin_datos ? "" : cp.nivel === "bueno" ? "acm-ok" : cp.nivel === "regular" ? "acm-ojo" : "acm-mal";
  return (
    <section className="acm-card acm-card-p space-y-3" data-testid="ac-como-viene">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="acm-titulo flex-1">Cómo viene con nosotros</h3>
        <div className="acm-tabs" role="tablist" aria-label="Gráfico">
          {[["deuda", "Deuda"], ["pagos", "Pagos"]].map(([k, t]) => (
            <button key={k} type="button" role="tab" aria-selected={ver === k} onClick={() => setVer(k)} className="acm-tab">{t}</button>
          ))}
        </div>
      </div>
      {ver === "deuda" ? <GraficoDeuda serie={serie} /> : <GraficoPagos anios={anios} tol={tol} />}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="acm-mini"><div className="acm-mini-t">Cómo nos paga</div><div className={"acm-mini-v " + color}>{cp.sin_datos ? "—" : `${cp.puntaje} / 100`}</div></div>
        <div className="acm-mini"><div className="acm-mini-t">Debe hoy</div><div className="acm-mini-v">{usdCorto(cp.deuda)}</div></div>
        <div className="acm-mini"><div className="acm-mini-t">Vencido hoy</div><div className={"acm-mini-v " + (cp.vencido > 0 ? "acm-mal" : "")}>{usdCorto(cp.vencido)}</div></div>
        <div className="acm-mini"><div className="acm-mini-t">Atraso promedio</div><div className="acm-mini-v">{cp.historial?.dias_prom ?? cp.dias_atraso_prom ?? "—"} días</div></div>
      </div>
    </section>
  );
}
